/**
 * Hover-intent prefetch of a card's detail query.
 *
 * Cards warm the Apollo cache with their detail query so the first tap into
 * the detail page paints instantly. That used to run on `mouseenter`, which
 * also fires for every card that slides under a STATIONARY pointer while the
 * page scrolls — so scrolling a grid with the mouse over it sent one detail
 * query per card, against the same rate-limit budget as the list itself.
 *
 *  - Pointer: prefetch only once it has rested on the card for
 *    HOVER_INTENT_MS. Leaving sooner (a card scrolling past) cancels it.
 *  - Keyboard focus: prefetch straight away — focus is deliberate.
 *  - At most once per card instance; a pending timer is cleared on unmount.
 *  - `cache-first`, and failures are swallowed — a prefetch never surfaces.
 *
 * Usage (inside a card component) — the queries the linked page runs, from
 * `entityPageRequests` (@/graphql/entityRoutes), which reads the same path as
 * the link: a `*ById` query, or for a slug path the resolution and then it
 * (web plan W0.16):
 *
 *   const prefetch = useHoverPrefetch(entityPageRequests('brand', brand));
 *   return <Link to={entityPath(brand, 'brand')} {...prefetch}>…</Link>;
 *
 * or one query: `useHoverPrefetch(BRAND_BY_ID, { id: brand.id })`.
 */
import { useEffect, useRef } from 'react';
import { useApolloClient, type OperationVariables } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import type { PrefetchRequest } from '@/graphql/entityRoutes';

/** How long the pointer must rest on a card before its detail is prefetched. */
export const HOVER_INTENT_MS = 200;

export interface HoverPrefetchHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
}

export function useHoverPrefetch(requests: readonly PrefetchRequest[]): HoverPrefetchHandlers;
export function useHoverPrefetch(
  query: DocumentNode,
  variables: OperationVariables,
): HoverPrefetchHandlers;
export function useHoverPrefetch(
  queryOrRequests: DocumentNode | readonly PrefetchRequest[],
  variables?: OperationVariables,
): HoverPrefetchHandlers {
  const requests: readonly PrefetchRequest[] = Array.isArray(queryOrRequests)
    ? queryOrRequests
    : [{ query: queryOrRequests as DocumentNode, variables: variables ?? {} }];
  const client = useApolloClient();
  const prefetched = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = () => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
  };

  // Unmounted mid-hover (navigated away, list re-rendered): never fire late.
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    },
    [],
  );

  const prefetch = () => {
    cancel();
    if (prefetched.current) return;
    prefetched.current = true;
    for (const { query, variables: vars } of requests) {
      client.query({ query, variables: vars, fetchPolicy: 'cache-first' }).catch(() => {});
    }
  };

  return {
    onMouseEnter: () => {
      if (prefetched.current || timer.current !== null) return;
      timer.current = setTimeout(prefetch, HOVER_INTENT_MS);
    },
    onMouseLeave: cancel,
    onFocus: prefetch,
  };
}
